import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import LogoNormal from 'assets/images/gt-logo-menu-normal.svg';
import { CountryFlagImage } from 'utils/render-countries-codes';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { PwaIcon, type PwaIconName } from './Icon';

export const PwaLogo: React.FC = () => (
  <img
    className="pwa-logo"
    src={LogoNormal}
    alt={process.env.PROJECT_NAME || 'generate.TAX'}
  />
);

type TopBarProps = {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  backLabel?: string;
  logo?: boolean;
  actions?: React.ReactNode;
};

export const PwaTopBar: React.FC<TopBarProps> = ({
  title,
  subtitle,
  onBack,
  backLabel = 'Back',
  logo,
  actions,
}) => (
  <header className="pwa-topbar">
    <div className="pwa-topbar-row">
      {onBack ? (
        <button
          type="button"
          className="pwa-iconbtn"
          onClick={onBack}
          aria-label={backLabel}
        >
          <PwaIcon name="back" />
        </button>
      ) : null}
      {logo || (!title && !onBack) ? (
        <div className="pwa-topbar-title with-logo">
          <PwaLogo />
        </div>
      ) : (
        <div className="pwa-topbar-title">
          <h1>{title}</h1>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      )}
      {actions}
    </div>
  </header>
);

type SectionProps = {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
};

export const PwaSection: React.FC<SectionProps> = ({
  title,
  action,
  children,
}) => (
  <section className="pwa-section">
    {title ? (
      <div className="pwa-section-head">
        <span>{title}</span>
        {action}
      </div>
    ) : null}
    {children}
  </section>
);

type ChipProps = {
  tone?: 'ok' | 'warn' | 'danger' | 'brand' | 'role' | 'info';
  icon?: PwaIconName | string;
  children: React.ReactNode;
};

export const PwaChip: React.FC<ChipProps> = ({ tone, icon, children }) => (
  <span className={`pwa-chip${tone ? ` ${tone}` : ''}`}>
    {icon ? <PwaIcon name={icon} size={13} stroke={2.2} /> : null}
    {children}
  </span>
);

type SearchProps = {
  id: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
};

export const PwaSearch: React.FC<SearchProps> = ({
  id,
  placeholder,
  value,
  onChange,
  'aria-label': ariaLabel,
}) => (
  <label className="pwa-search" htmlFor={id}>
    <PwaIcon name="search" size={19} />
    <input
      id={id}
      type="search"
      placeholder={placeholder}
      value={value}
      aria-label={ariaLabel || placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

type SheetProps = {
  title: string;
  sub?: string;
  onClose: () => void;
  closeLabel?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Nearly full-height sheet (history / rules). */
  full?: boolean;
  /** Extra class on the sheet panel (e.g. stacking above drawers). */
  className?: string;
  dragZoneRef?: React.RefObject<HTMLDivElement | null>;
  dragCloseHandlers?: Pick<
    React.HTMLAttributes<HTMLElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
  > & { style?: React.CSSProperties };
};

export const PwaSheet: React.FC<SheetProps> = ({
  title,
  sub,
  onClose,
  closeLabel,
  children,
  footer,
  full = false,
  className,
  dragZoneRef,
  dragCloseHandlers,
}) => {
  const { t } = useTranslation();
  const resolvedCloseLabel = closeLabel ?? t('common.close');
  const internalDragZoneRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const sheetRef = React.useRef<HTMLElement>(null);
  const zoneRef = dragZoneRef ?? internalDragZoneRef;
  const ownsDrag = !dragCloseHandlers;
  const internalDrag = usePwaBottomSheetDragClose(
    true,
    onClose,
    zoneRef,
    bodyRef
  );
  const handlers = ownsDrag ? internalDrag : dragCloseHandlers;

  React.useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const focusable = () =>
      Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) =>
          !el.hasAttribute('disabled') &&
          el.tabIndex >= 0 &&
          el.getAttribute('aria-hidden') !== 'true'
      );
    const items = focusable();
    items[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const current = focusable();
      if (!current.length) return;
      const first = current[0];
      const last = current[current.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !sheet.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !sheet.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [title]);

  if (typeof document === 'undefined') {
    return null;
  }

  /* Portal to body so stacking matches Ant drawers (above bottom nav / app-shell). */
  return createPortal(
    <div
      data-shell="pwa"
      className={`pwa-overlay-root${className ? ` ${className}-root` : ''}`}
    >
      <div className="pwa-scrim" onClick={onClose} aria-hidden />
      <section
        ref={sheetRef}
        className={`pwa-sheet scope-pwa-bottom-sheet${full ? ' full' : ''}${
          className ? ` ${className}` : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          ref={zoneRef}
          className="pwa-sheet-chrome scope-pwa-bottom-sheet__drag-zone"
          {...handlers}
        >
          <div className="pwa-sheet-grab scope-pwa-bottom-sheet__drag-handle" />
          <div className="pwa-sheet-head">
            <div>
              <h2>{title}</h2>
              {sub ? <p>{sub}</p> : null}
            </div>
            <button
              type="button"
              className="pwa-iconbtn filled"
              onClick={onClose}
              aria-label={resolvedCloseLabel}
            >
              <PwaIcon name="close" size={20} />
            </button>
          </div>
        </div>
        <div className="pwa-sheet-body" ref={bodyRef}>
          {children}
        </div>
        {footer ? <div className="pwa-sheet-foot">{footer}</div> : null}
      </section>
    </div>,
    document.body
  );
};

type RowProps = {
  icon?: PwaIconName | string;
  iconTone?: 'neutral';
  lead?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** Status row under sub (chips + secondary text). */
  status?: React.ReactNode;
  meta?: React.ReactNode;
  end?: React.ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  wrap?: boolean;
  disabled?: boolean;
};

export const PwaRow: React.FC<RowProps> = ({
  icon,
  iconTone,
  lead,
  title,
  sub,
  status,
  meta,
  end,
  chevron,
  onClick,
  wrap,
  disabled,
}) => {
  const className = `pwa-row${onClick && !disabled ? '' : ' static'}${
    disabled ? ' pwa-disabled' : ''
  }`;
  const body = (
    <>
      {lead}
      {icon ? (
        <span
          className={`pwa-row-ico${iconTone ? ` ${iconTone}` : ''}`}
          aria-hidden
        >
          <PwaIcon name={icon} size={20} />
        </span>
      ) : null}
      <span className="pwa-row-body">
        <span className={`pwa-row-title${wrap ? ' wrap' : ''}`}>{title}</span>
        {sub ? (
          <span className={`pwa-row-sub${wrap ? ' wrap' : ''}`}>{sub}</span>
        ) : null}
        {status ? <span className="pwa-row-status">{status}</span> : null}
        {meta ? <span className="pwa-row-meta">{meta}</span> : null}
      </span>
      {end || chevron ? (
        <span className="pwa-row-end">
          {end}
          {chevron ? <PwaIcon name="chevron" size={18} /> : null}
        </span>
      ) : null}
    </>
  );

  if (onClick && !disabled) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {body}
      </button>
    );
  }

  return <div className={className}>{body}</div>;
};

type SwitchProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
};

export const PwaSwitch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  label,
  disabled,
}) => (
  <button
    type="button"
    className="pwa-switch"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(event) => {
      event.stopPropagation();
      if (disabled) return;
      onChange(!checked);
    }}
  />
);

type FilterChipItem = {
  value: string;
  label: string;
  count?: number;
  icon?: PwaIconName | string;
};

type FilterChipsProps = {
  items: FilterChipItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  stretch?: boolean;
};

export const PwaFilterChips: React.FC<FilterChipsProps> = ({
  items,
  value,
  onChange,
  className,
  stretch = false,
}) => {
  const { t } = useTranslation();
  return (
  <div
    className={[
      'pwa-filters',
      stretch ? 'pwa-filters--stretch' : null,
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    role="toolbar"
    aria-label={t('main.filters')}
  >
    {items.map((item) => (
      <button
        key={item.value}
        type="button"
        className="pwa-fchip"
        aria-pressed={value === item.value}
        onClick={() => onChange(item.value)}
      >
        {item.icon ? <PwaIcon name={item.icon} size={15} /> : null}
        {item.label}
        {item.count != null ? <span className="count">{item.count}</span> : null}
      </button>
    ))}
  </div>
  );
};

type ThumbProps = {
  sm?: boolean;
};

export const PwaThumb: React.FC<ThumbProps> = ({ sm }) => (
  <span className={`pwa-thumb${sm ? ' sm' : ''}`} aria-hidden />
);

type SegmentedOption = {
  value: string;
  label: string;
  count?: number;
};

type SegmentedProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SegmentedOption[];
};

export const PwaSegmented: React.FC<SegmentedProps> = ({
  label,
  value,
  onChange,
  options,
}) => (
  <div className="pwa-seg" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
        {option.count != null ? (
          <span className="count">{option.count}</span>
        ) : null}
      </button>
    ))}
  </div>
);

type AmountProps = {
  value: number;
  currency?: string;
};

export const PwaAmount: React.FC<AmountProps> = ({
  value,
  currency = '£',
}) => {
  const neg = value < 0;
  const formatted = Math.abs(value).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <span className={`pwa-amount${neg ? ' pwa-neg' : ''}`}>
      {neg ? '−' : ''}
      {currency}
      {formatted}
    </span>
  );
};

type EmptyProps = {
  title: string;
  icon?: PwaIconName | string;
  children?: React.ReactNode;
};

export const PwaEmpty: React.FC<EmptyProps> = ({
  title,
  icon = 'check',
  children,
}) => (
  <div className="pwa-empty">
    <span className="ico" aria-hidden>
      <PwaIcon name={icon} size={26} />
    </span>
    <h3>{title}</h3>
    {children ? <p>{children}</p> : null}
  </div>
);

type FieldOption = {
  value: string;
  label: string;
  /** Secondary text after the label (e.g. dial code; not bold). */
  detail?: string;
  /** Optional country flag for phone-code pickers (desktop Select style). */
  flag?: { src?: string; alt?: string; cca2?: string };
};

type FieldProps = {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  onChange?: (value: string) => void;
  full?: boolean;
  select?: boolean;
  /**
   * Long option lists (e.g. time zones): open a searchable bottom sheet instead
   * of a native `<select>`, which overflows off-screen on phones.
   */
  sheetSelect?: boolean;
  options?: FieldOption[];
  /** Closed-field text for sheet pickers (e.g. `+356` while list shows country names). */
  triggerLabel?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  hint?: string;
  type?: string;
  readOnly?: boolean;
};

export const PwaField: React.FC<FieldProps> = ({
  id,
  label,
  required,
  value,
  onChange,
  full,
  select,
  sheetSelect,
  options,
  triggerLabel,
  searchPlaceholder,
  emptyLabel,
  hint,
  type = 'text',
  readOnly,
}) => {
  const { t } = useTranslation();
  const resolvedEmptyLabel = emptyLabel ?? t('common.not-set', { defaultValue: 'Not set' });
  const resolvedSearchPlaceholder = String(
    searchPlaceholder || t('common.search')
  );
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selectedLabel = React.useMemo(() => {
    if (triggerLabel != null && triggerLabel !== '') return triggerLabel;
    const match = (options || []).find((opt) => opt.value === value);
    return match?.label || value || '';
  }, [options, value, triggerLabel]);

  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q) ||
        (opt.detail || '').toLowerCase().includes(q) ||
        (opt.flag?.cca2 || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  React.useEffect(() => {
    if (!sheetOpen) setQuery('');
  }, [sheetOpen]);

  if (sheetSelect) {
    return (
      <div className={`pwa-field${full ? ' full' : ''}`}>
        <label htmlFor={id}>
          {label}
          {required ? <span className="req">*</span> : null}
        </label>
        <button
          type="button"
          id={id}
          className={`pwa-input pwa-input--sheet-trigger${
            readOnly ? ' readonly' : ''
          }${!selectedLabel ? ' placeholder' : ''}`}
          disabled={readOnly}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          onClick={() => {
            if (!readOnly) setSheetOpen(true);
          }}
        >
          <span className="pwa-input__value">
            {selectedLabel || resolvedEmptyLabel}
          </span>
          <PwaIcon name="down" size={18} />
        </button>
        {hint ? <span className="hint">{hint}</span> : null}
        {sheetOpen ? (
          <PwaSheet
            title={label}
            onClose={() => setSheetOpen(false)}
            full
            className="pwa-option-picker-sheet"
          >
            <PwaSearch
              id={`${id}-search`}
              placeholder={resolvedSearchPlaceholder}
              value={query}
              onChange={setQuery}
            />
            <div className="pwa-list pwa-option-picker-list">
              {filteredOptions.length === 0 ? (
                <p className="pwa-row-meta" style={{ margin: '8px 4px' }}>
                  {t('common.no-matches', { defaultValue: 'No matches' })}
                </p>
              ) : (
                filteredOptions.map((opt) => {
                  const selected = opt.value === value;
                  return (
                    <button
                      key={`${opt.value}::${opt.flag?.cca2 || opt.label}`}
                      type="button"
                      className={`pwa-row${selected ? ' selected' : ''}`}
                      onClick={() => {
                        onChange?.(opt.value);
                        setSheetOpen(false);
                      }}
                    >
                      {opt.flag ? (
                        <CountryFlagImage
                          src={opt.flag.src}
                          alt={opt.flag.alt || opt.label}
                          cca2={opt.flag.cca2 || ''}
                        />
                      ) : null}
                      <span className="pwa-row-body">
                        <span className="pwa-row-title wrap">
                          {opt.label}
                          {opt.detail ? (
                            <span className="pwa-option-detail">
                              {' '}
                              {opt.detail}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </PwaSheet>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`pwa-field${full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="req">*</span> : null}
      </label>
      {select ? (
        <select
          id={id}
          className="pwa-input"
          value={value}
          disabled={readOnly}
          onChange={(event) => onChange?.(event.target.value)}
        >
          {(options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className="pwa-input"
          type={type}
          value={value}
          readOnly={readOnly}
          onChange={(event) => onChange?.(event.target.value)}
        />
      )}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
};

type ProviderMarkProps = {
  id: 'xero' | 'qbo' | 'zoho' | string;
  lg?: boolean;
};

const PROVIDER_MARKS: Record<string, { bg: string; t: string }> = {
  xero: { bg: '#13B5EA', t: 'X' },
  qbo: { bg: '#2CA01C', t: 'qb' },
  zoho: { bg: '#E42527', t: 'Z' },
};

export const PwaProviderMark: React.FC<ProviderMarkProps> = ({ id, lg }) => {
  const mark = PROVIDER_MARKS[id] || PROVIDER_MARKS.xero;
  return (
    <span
      className={`pwa-prov${lg ? ' lg' : ''}`}
      style={{ background: mark.bg }}
      aria-hidden
    >
      {mark.t}
    </span>
  );
};

type UserPicProps = {
  initials: string;
  size?: 'sm' | 'xl';
  src?: string | null;
};

export const PwaUserPic: React.FC<UserPicProps> = ({
  initials,
  size,
  src,
}) => {
  if (src) {
    return (
      <span
        className={`pwa-userpic${size ? ` ${size}` : ''} pwa-userpic--photo`}
        aria-hidden
      >
        <img src={src} alt="" />
      </span>
    );
  }
  return (
    <span className={`pwa-userpic${size ? ` ${size}` : ''}`} aria-hidden>
      {initials}
    </span>
  );
};
